import { useUsageLimits } from "@/hooks/useUsageLimits";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Search, FileSignature, Share2, Zap, Crown, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

type DisplayUsageType = "documents" | "ai_searches" | "pdf_actions" | "share_links";

const USAGE_CONFIG: Record<DisplayUsageType, { 
  icon: React.ElementType; 
  label: string; 
  period: string;
  color: string;
}> = {
  documents: { 
    icon: FileText, 
    label: "Documents", 
    period: "",
    color: "text-blue-500"
  },
  ai_searches: { 
    icon: Search, 
    label: "Recherches IA", 
    period: "aujourd'hui",
    color: "text-purple-500"
  },
  pdf_actions: { 
    icon: FileSignature, 
    label: "Actions PDF", 
    period: "ce mois",
    color: "text-orange-500"
  },
  share_links: { 
    icon: Share2, 
    label: "Liens de partage", 
    period: "",
    color: "text-green-500"
  },
};

export function DashboardUsageBanner() {
  const { 
    usage, 
    limits, 
    planId, 
    isLoading, 
    getUsagePercentage, 
    getRemainingUsage,
    canUseFeature 
  } = useUsageLimits();
  const navigate = useNavigate();

  if (isLoading) return null;

  const isDiscovery = planId === "discovery";
  const usageTypes: DisplayUsageType[] = ["documents", "ai_searches", "pdf_actions", "share_links"];
  
  // Check if any limit is near or at capacity
  const hasWarning = usageTypes.some(type => {
    const limit = limits[type];
    if (limit === -1) return false;
    return getUsagePercentage(type) >= 80;
  });

  const hasLimit = usageTypes.some(type => {
    const limit = limits[type];
    if (limit === -1) return false;
    return getUsagePercentage(type) >= 100;
  });

  // Plan display name
  const planDisplayName: Record<string, string> = {
    discovery: "Découverte",
    solo: "Solo",
    famille: "Famille",
    pro: "Pro",
    team: "Team",
    business: "Business",
    enterprise: "Enterprise",
  };
  const displayName = planDisplayName[planId] || planId;

  return (
    <Card className={`
      border transition-colors
      ${hasLimit ? "border-destructive/50 bg-destructive/5" : 
        hasWarning ? "border-warning/50 bg-warning/5" : 
        "border-border bg-card"}
    `}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Badge variant={isDiscovery ? "secondary" : "default"} className="gap-1">
              <Crown className="w-3 h-3" />
              {displayName}
            </Badge>
            {isDiscovery && (
              <span className="text-xs text-muted-foreground">Gratuit</span>
            )}
          </div>
          <Button 
            size="sm" 
            variant={hasLimit ? "destructive" : hasWarning ? "default" : "outline"}
            onClick={() => navigate("/pricing")}
            className="gap-1"
          >
            <Zap className="w-3 h-3" />
            {hasLimit ? "Limite atteinte" : "Upgrade"}
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {usageTypes.map(type => {
            const config = USAGE_CONFIG[type];
            const Icon = config.icon;
            const limit = limits[type];
            const isUnlimited = limit === -1;
            const percentage = getUsagePercentage(type);
            const remaining = getRemainingUsage(type);
            const isNear = percentage >= 80 && percentage < 100;
            const isAt = percentage >= 100;

            if (isUnlimited) return null;

            return (
              <div key={type} className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                  <span className="text-xs font-medium truncate">{config.label}</span>
                </div>
                <Progress 
                  value={percentage} 
                  className={`h-1.5 ${isAt ? "[&>div]:bg-destructive" : isNear ? "[&>div]:bg-warning" : ""}`}
                />
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] ${isAt ? "text-destructive" : "text-muted-foreground"}`}>
                    {usage[type]}/{limit}
                  </span>
                  {config.period && (
                    <span className="text-[10px] text-muted-foreground">{config.period}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Locked features for discovery plan */}
        {isDiscovery && (
          <div className="mt-3 pt-3 border-t border-border/50 flex flex-wrap gap-2">
            {!canUseFeature("export") && (
              <Badge variant="outline" className="text-[10px] gap-1">
                🔒 Export
              </Badge>
            )}
            {!canUseFeature("team") && (
              <Badge variant="outline" className="text-[10px] gap-1">
                🔒 Équipe
              </Badge>
            )}
            <button 
              onClick={() => navigate("/pricing")}
              className="text-[10px] text-primary hover:underline flex items-center gap-0.5"
            >
              Débloquer <ArrowRight className="w-2.5 h-2.5" />
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}